import { BrandDocument } from '@models/brand';
import { ProductDocument } from '@models/product';
import { BreadcrumbItem } from '@ui/Breadcrumb';
import { getGenderLabel } from '@util/createListingHelpers';
import { formatTimestamp } from '@util/index';
import { slugify } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import Link from 'next/link';
const ProductOverview = ({
  product,
  brand,
  breadcrumbs,
}: {
  product: ProductDocument;
  brand?: BrandDocument | null;
  breadcrumbs: BreadcrumbItem[];
}) => {
  const { userDoc } = useAuth();

  return (
    <div className="w-full px-8 sm:px-0">
      <h2 className="mb-[1rem] mt-[4rem] text-[1.8rem] font-semibold sm:mt-[2rem]">
        Overview
      </h2>

      <div className="flex w-full flex-col gap-x-16 gap-y-4 whitespace-nowrap">
        {!!product.condition && (
          <div className="flex flex-row">
            <p className="w-[14rem] text-[1.6rem]">Condition</p>

            <span className="grow text-[1.6rem] font-semibold text-brand-black">
              {product.condition}
            </span>
          </div>
        )}
        {product.gender &&
          (product.category === 'Riding Gear' ||
            (product.category === 'Mountain Bikes' &&
              product.category1 === 'Gear')) && (
            <div className="flex flex-row">
              <p className="min-w-[14rem] text-[1.6rem]">Gender:</p>

              <p className="grow text-[1.6rem] text-brand-black">
                {getGenderLabel(product.gender).trim()}
              </p>
            </div>
          )}
        {(product.size?.letter || product.size?.number) &&
          !product.has_variations && (
            <div className="flex flex-row">
              <p className="min-w-[14rem] text-[1.6rem]">Size:</p>

              <p className="grow text-[1.6rem] text-brand-black">
                {product.size?.letter
                  ? product.size.letter
                  : product.size?.number
                  ? product.size.number.toString()
                  : ''}
              </p>
            </div>
          )}
        <div className="flex">
          <p className="w-[14rem] shrink-0 text-[1.5rem]">Category:</p>

          <div className="flex w-full flex-wrap gap-[0.8rem] overflow-x-hidden">
            {breadcrumbs.length === 1 &&
            breadcrumbs.length !== product.categories.length
              ? product.categories.map((category) => (
                  <p key={category} className="text-[1.5rem] text-black">
                    {category}
                    {product.categories.indexOf(category) !==
                    product.categories.length - 1
                      ? ','
                      : ''}
                  </p>
                ))
              : breadcrumbs.map((b) => (
                  <Link
                    href={b.href}
                    key={b.label}
                    className="text-[1.5rem] text-black underline"
                  >
                    {b.label}

                    {breadcrumbs.indexOf(b) !== breadcrumbs.length - 1
                      ? ','
                      : ''}
                  </Link>
                ))}
          </div>
        </div>
        <div className="flex">
          <p className="mt-[0.5rem] w-[14rem] text-[1.5rem]">Brand:</p>

          {brand?.url || product.brand === 'OEM' ? (
            <Link
              className="mt-[0.5rem] grow font-semibold text-brand-black underline"
              href={
                product.brand === 'OEM'
                  ? '/shop/oem-dirt-bike-parts'
                  : `/brands/${brand?.url}`
              }
            >
              {product.brand}
            </Link>
          ) : (
            <Link
              href={`/brands/${slugify(product.brand)}`}
              className="mt-[0.5rem] grow font-semibold text-brand-black underline"
            >
              {product.brand}
            </Link>
          )}
        </div>
        {product.ride_types.length > 0 && (
          <div className="flex flex-row">
            <p className="w-[14rem] text-[1.6rem]">Ride Type:</p>

            <div className="flex flex-wrap gap-x-2">
              {product.ride_types.map((ride_type) => (
                <Link
                  key={ride_type}
                  href={`/shop/${slugify(ride_type)}`}
                  className="text-[1.6rem] font-semibold text-brand-black underline"
                >
                  {ride_type}
                  {product.ride_types.indexOf(ride_type) !==
                  product.ride_types.length - 1
                    ? ','
                    : ''}
                </Link>
              ))}
            </div>
          </div>
        )}

        {product.sku && !product.variations?.length && (
          <div className="flex flex-row">
            <p className="w-[14rem] text-[1.6rem]">SKU:</p>
            <span className="grow text-[1.6rem] font-semibold text-brand-black">
              {product.sku}
            </span>
          </div>
        )}

        {(userDoc?.roles?.admin ||
          (product.seller_id === userDoc?.uid && userDoc?.roles?.reseller)) &&
          !!product.minimum_offer_price && (
            <div className="flex flex-row">
              <p className="w-[14rem] text-[1.6rem]">MOP:</p>
              <span className="grow text-[1.6rem] font-semibold text-brand-black">
                {product.minimum_offer_price}
              </span>
            </div>
          )}
        {(userDoc?.roles?.admin ||
          userDoc?.uid === 'E16WuBcn0UMejlp0FSdQulSSed12') &&
          product.integration_info && (
            <div className="flex flex-row">
              <p className="w-[14rem] text-[1.6rem]">Synced:</p>
              <span className="grow text-[1.6rem] font-semibold text-brand-black">
                {product.integration_info.type}
              </span>
            </div>
          )}
        {!!product.item_number && (
          <div className="flex flex-row">
            <p className="w-[14rem] text-[1.6rem]">Item #</p>

            <span className="grow text-[1.6rem] font-semibold text-brand-black">
              {product.item_number}
            </span>
          </div>
        )}
        {!!product.mpn?.length && !product.variations?.length && (
          <div className="flex flex-row">
            <p className="w-[14rem] shrink-0 text-[1.6rem]">MPN:</p>

            <div className="flex flex-wrap gap-x-2">
              {product.mpn.map((m, i) => (
                <span
                  key={m + i}
                  className="text-[1.6rem] font-semibold text-brand-black"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>
        )}
        {product.seller_id === userDoc?.uid && product.date_added && (
          <div className="flex flex-row">
            <p className="w-[14rem] text-[1.6rem]">Listed Date:</p>

            <span className="grow text-[1.6rem] font-semibold text-brand-black">
              {formatTimestamp(product.date_added)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductOverview;
